
import { Component, Output, EventEmitter, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { DateRangePayload, FilterDateRangeService } from '../../../services/filterdaterange.service';
import { MatDatepicker } from '@angular/material/datepicker';
import { FilterSideNavComponent } from '../filter-side-nav/filter-side-nav.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-order-date-range',
  templateUrl: './order-date-range.component.html',
  styleUrl: './order-date-range.component.scss',
})
export class OrderDateRangeComponent implements OnInit, OnDestroy {
  @Output() navigateBack = new EventEmitter<void>();
  @Output() rangeSelected = new EventEmitter<DateRangePayload>();
  

@ViewChild('pickerB') pickerB!: MatDatepicker<Date>;
@ViewChild('pickerA') pickerA!: MatDatepicker<Date>;
@ViewChild('filterSideNav') filterSideNav!: FilterSideNavComponent;

private wasFiltersCleared = false;
private destroy$ = new Subject<void>();


/**
 * Generic clear handler for any datepicker
 */
onClear(controlName: 'currentDate' | 'pastDate', picker: MatDatepicker<Date>): void {
  this.dateRangeForm.get(controlName)?.setValue(null);
  picker.close();
}

/**
 * Generic cancel handler for any datepicker
 */
onCancel(picker: MatDatepicker<Date>): void {
  picker.close();
}



  // Date constraints
  readonly today = this.stripTime(new Date());
  readonly twoYearsAgo = this.addYears(this.today, -2);
  readonly ninetyDaysAgo = new Date(this.today.getTime() - 90 * 24 * 60 * 60 * 1000);
  readonly minDate = this.twoYearsAgo;
  readonly maxDate = this.today;

  // Form with default values
  dateRangeForm = new FormGroup({
    currentDate: new FormControl<Date | null>(this.today),
    pastDate: new FormControl<Date | null>(this.ninetyDaysAgo),
  });

  constructor(private filterDateRangeService: FilterDateRangeService, private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    // Load current values from service if available
    this.loadCurrentDatesFromService();
    
    // Reset wasFiltersCleared flag when user selects new dates
    this.dateRangeForm.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => {
          return prev.currentDate === curr.currentDate && prev.pastDate === curr.pastDate;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.currentDate !== null || this.pastDate !== null) {
          this.wasFiltersCleared = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCurrentDatesFromService(): void {
    // Subscribe to get current values from service and update form
    this.filterDateRangeService.dateRangeSelected$
      .pipe(takeUntil(this.destroy$))
      .subscribe((range) => {
        if (range && range.startDate && range.endDate) {
          // Convert yyyy-mm-dd strings back to Date objects for the form
          const startDate = this.parseYMD(range.startDate);
          const endDate = this.parseYMD(range.endDate);
          
          this.dateRangeForm.patchValue({
            pastDate: startDate,
            currentDate: endDate
          }, { emitEvent: false }); // Prevent triggering valueChanges unnecessarily
        }
      });
  }

  private parseYMD(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private addYears(date: Date, years: number): Date {
    const d = new Date(date);
    const targetYear = d.getFullYear() + years;
    const result = new Date(targetYear, d.getMonth(), d.getDate());
    
    // Handle month rollover (e.g., Feb 29 -> Mar 1)
    if (result.getMonth() !== d.getMonth()) {
      return new Date(targetYear, d.getMonth() + 1, 0);
    }
    return this.stripTime(result);
  }

  // Helpers to read form values
  get currentDate() { 
    return this.dateRangeForm.get('currentDate')?.value; 
  }
  
  get pastDate() { 
    return this.dateRangeForm.get('pastDate')?.value; 
  }

  private toYMD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  onBack(): void {
    // Use setTimeout to ensure form is updated before reading values
    setTimeout(() => {
      const start = this.pastDate ? this.toYMD(this.pastDate) : '';
      const end = this.currentDate ? this.toYMD(this.currentDate) : '';

      // If clear button was clicked AND dates are still empty, set 90-day defaults
      if (this.wasFiltersCleared && this.currentDate === null && this.pastDate === null) {
        console.log('Clear button was clicked, setting 90-day defaults...');
        this.dateRangeForm.patchValue({
          currentDate: this.today,
          pastDate: this.ninetyDaysAgo
        });
        this.wasFiltersCleared = false;
        
        // Use the defaults
        const defaultStart = this.toYMD(this.ninetyDaysAgo);
        const defaultEnd = this.toYMD(this.today);
        
        // Save 90-day defaults to service
        console.log('Saving 90-day default range to service...');
        console.log('Default Start:', defaultStart, 'Default End:', defaultEnd);
        this.filterDateRangeService.setCustomDateRange(defaultStart, defaultEnd);
      } 
      else {
        // Dates were selected from service, validate them
        console.log('Using dates selected from service...');
        
        // Validate that pastDate (start) is not greater than currentDate (end)
        const isInvalid = this.pastDate && this.currentDate && this.pastDate > this.currentDate;
        
        if (isInvalid) {
          console.error('Validation Error: currentDate cannot be greater than fromDate');
          const errorMessage = 'Error: current Date cannot be greater than from Date. Please select valid dates.';
          
          this.snackBar.open(errorMessage, 'Close', { 
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
          });
          
          return;
        }

        // Save the selected dates to service to persist them
        if (start && end) {
          console.log('Saving selected dates to service...');
          console.log('Start:', start, 'End:', end);
          this.filterDateRangeService.setCustomDateRange(start, end);
        }
      }

      // Navigate back to filter-side-nav (NO filter application - only navigation)
      console.log('Navigating back to filter-side-nav...');
      this.navigateBack.emit();
    }, 0);
  }
  

  

  clearFilters(): void {
    this.dateRangeForm.patchValue({
      currentDate: null,
      pastDate: null
    });
    this.wasFiltersCleared = true;
  }

  applyFilters(): void {
    // If both dates are null, use 90-day default
    if (this.currentDate === null && this.pastDate === null) {
      this.dateRangeForm.patchValue({
        currentDate: this.today,
        pastDate: this.ninetyDaysAgo
      });
      // Use setTimeout to ensure form values are updated before onSelect
      setTimeout(() => {
        this.onSelect();
      }, 0);
    } else {
      this.onSelect();
    }
  }

  onSelect(): void {
    // Return early if either date is null
    if (!this.pastDate || !this.currentDate) {
      console.log('Start or end date is null, returning early');
      return;
    }

    const start = this.toYMD(this.pastDate);
    const end = this.toYMD(this.currentDate);

    console.log('=== onSelect Debug ===');
    console.log('pastDate:', this.pastDate);
    console.log('currentDate:', this.currentDate);
    console.log('start (formatted):', start);
    console.log('end (formatted):', end);


    // Validate that pastDate (start) is not greater than currentDate (end)
    const isInvalid = this.pastDate > this.currentDate;
    console.log('Is Invalid (pastDate > currentDate):', isInvalid);
    
    if (isInvalid) {
      console.error('Validation Error: currentDate  cannot be greater than fromDate');
      const errorMessage = 'Error: current Date cannot be greater than from Date. Please select valid dates.';
      console.log('Showing error:', errorMessage);
      
      // Use alert immediately
      
      // Also try snackbar with delay
      setTimeout(() => {
        this.snackBar.open(errorMessage, 'Close', { 
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        });
      }, 100);
      
      return;
    }

    console.log('Validation passed. Proceeding...');
    // Update the service with selected dates
    this.filterDateRangeService.setCustomDateRange(start, end);
    // Emit the range to parent component (if parent needs it via @Output)
    this.rangeSelected.emit({ startDate: start, endDate: end });
    // Navigate back after selection
    this.navigateBack.emit();
  }
}